/**
 * The full plan-tree read, as `queryOptions`.
 *
 * One read, and it is unlike every other read in this app: the response runs to roughly seventeen
 * thousand set rows, and the moment it lands the DRAFT becomes the source of truth. Execution ADR
 * §4 settles the policy, and each part of it is load-bearing rather than tuning:
 *
 * · **`staleTime: Infinity`** — once loaded, the draft owns the tree. A background refetch would
 *   either be discarded or would silently replace the trainer's unsaved work, and the second one
 *   is a data-loss bug that looks like a rendering glitch.
 * · **`refetchOnWindowFocus: false`** — the same hazard, on the most ordinary action there is.
 *   A trainer alt-tabs to a spreadsheet and comes back; that must not touch the document.
 * · **`structuralSharing: false`** — diffing a tree this size on every settle costs more than it
 *   saves when nothing re-reads it. The default exists to keep object identity stable for
 *   re-render avoidance; here the consumer is a `select` that runs once.
 *
 * ── `lang` is in the key, and its absence is silent ──────────────────────────────────────────
 * Every exercise name in the tree is server-localised. A blank `lang` resolves to English rather
 * than erroring, so an omission does not fail — the trainer simply sees "Barbell Bench Press" in a
 * Polish interface. Keying on it is what stops the first language fetched being pinned for the life
 * of the session. `src/shared/api/generatedContract.test.ts` pins that the generated params carry
 * `lang` at all, so a regen that dropped it stops compiling.
 *
 * ── Hand-written rather than orval's `getGetPlanTemplateIdFullQueryOptions` ──────────────────
 * Same reasoning as `pages/plan-library/api/planLibraryQueries.ts`: the key and the caching policy
 * are decisions this slice owns, and delegating them would hand both to whatever orval emits next.
 * The three options above are the entire point of this file; a generated wrapper carries none of
 * them.
 */

import { queryOptions } from '@tanstack/react-query';

import { getPlanTemplateIdFull } from '~/shared/api/generated/endpoints/plan-template/plan-template';
import type { ContentLanguage } from '~/shared/i18n/contentLanguage';

/** Every plan's tree at once — what a write reaching plans the caller cannot enumerate drops. */
export const planTemplateFullPrefix = ['plan-template', 'full'] as const;

/**
 * The language-blind prefix.
 *
 * Invalidating `['plan-template', 'full', id]` drops every language's copy of one plan at once,
 * which is what a save wants: the tree changed, so neither the Polish nor the English render of it
 * is still true. Exported as the prefix rather than assembled at the call site so PR-B1's
 * invalidation and this key cannot drift.
 */
export function planTemplateFullRoot(planId: number) {
  return [...planTemplateFullPrefix, planId] as const;
}


export function planTemplateFullQueryKey(planId: number, lang: ContentLanguage) {
  return [...planTemplateFullRoot(planId), lang] as const;
}

export function planTemplateFullQueryOptions(planId: number, lang: ContentLanguage) {
  return queryOptions({
    queryKey: planTemplateFullQueryKey(planId, lang),
    queryFn: ({ signal }) => getPlanTemplateIdFull(planId, { lang }, undefined, signal),
    staleTime: Infinity,
    structuralSharing: false,
    refetchOnWindowFocus: false,
  });
}
